import { useState, useCallback } from 'react';
import { addMonths, subMonths, addDays, subDays } from '@/lib/dates';
import { useEvents } from '@/hooks/useEvents';
import { CalendarHeader } from '@/components/CalendarHeader';
import { CalendarGrid } from '@/components/CalendarGrid';
import { WeekView } from '@/components/WeekView';
import { DayView } from '@/components/DayView';
import { SmartInput } from '@/components/SmartInput';
import { EventDetail } from '@/components/EventDetail';
import type { CalendarEvent, ViewMode } from '@/types';
import styles from './App.module.css';

export function App() {
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const [smartInputOpen, setSmartInputOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [detailEvent, setDetailEvent] = useState<CalendarEvent | null>(null);

  const { events, syncStatus, addEvent, updateEvent, deleteEvent } = useEvents();

  const handlePrevMonth  = useCallback(() => setCurrentDate(d => subMonths(d, 1)), []);
  const handleNextMonth  = useCallback(() => setCurrentDate(d => addMonths(d, 1)), []);
  const handlePrevWeek   = useCallback(() => setSelectedDate(d => subDays(d, 7)), []);
  const handleNextWeek   = useCallback(() => setSelectedDate(d => addDays(d, 7)), []);
  const handleWeekDateChange  = useCallback((date: Date) => setSelectedDate(date), []);
  const handleDayDateChange   = useCallback((date: Date) => setSelectedDate(date), []);
  const handleMonthChange     = useCallback((date: Date) => setCurrentDate(date), []);
  const handlePrevDay    = useCallback(() => setSelectedDate(d => subDays(d, 1)), []);
  const handleNextDay    = useCallback(() => setSelectedDate(d => addDays(d, 1)), []);

  const handleToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
    setViewMode('month');
  }, []);

  // Month → Week (click a date)
  const handleSelectDateInMonth = useCallback((date: Date) => {
    setSelectedDate(date);
    setCurrentDate(date);
    setViewMode('week');
  }, []);

  // Week header → Day
  const handleSelectDayInWeek = useCallback((date: Date) => {
    setSelectedDate(date);
    setViewMode('day');
  }, []);

  const handleBack = useCallback(() => {
    if (viewMode === 'day')  setViewMode('week');
    if (viewMode === 'week') setViewMode('month');
  }, [viewMode]);

  const handleOpenAdd  = useCallback(() => setSmartInputOpen(true), []);

  const handleOpenEdit = useCallback((event: CalendarEvent) => {
    setEditingEvent(event);
    setDetailEvent(null);
    setSmartInputOpen(true);
  }, []);

  const handleSaveInput = useCallback(async (event: CalendarEvent) => {
    if (editingEvent) {
      await updateEvent(event);
    } else {
      await addEvent(event);
    }
    setSmartInputOpen(false);
    setEditingEvent(null);
  }, [addEvent, updateEvent, editingEvent]);

  const handleDelete = useCallback(async (id: string) => {
    await deleteEvent(id);
    setDetailEvent(null);
  }, [deleteEvent]);

  const handleEventClick = useCallback((event: CalendarEvent) => {
    setDetailEvent(event);
  }, []);

  return (
    <div className={styles.app}>
      <CalendarHeader
        viewMode={viewMode}
        currentDate={currentDate}
        selectedDate={selectedDate}
        syncStatus={syncStatus}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onPrevWeek={handlePrevWeek}
        onNextWeek={handleNextWeek}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onToday={handleToday}
        onBack={handleBack}
        onAdd={handleOpenAdd}
      />

      <main className={styles.main}>
        {viewMode === 'month' && (
          <CalendarGrid
            currentDate={currentDate}
            selectedDate={selectedDate}
            events={events}
            onSelectDate={handleSelectDateInMonth}
            onMonthChange={handleMonthChange}
          />
        )}
        {viewMode === 'week' && (
          <WeekView
            selectedDate={selectedDate}
            events={events}
            onSelectDay={handleSelectDayInWeek}
            onEventClick={handleEventClick}
            onDateChange={handleWeekDateChange}
            onPrevWeek={handlePrevWeek}
            onNextWeek={handleNextWeek}
          />
        )}
        {viewMode === 'day' && (
          <DayView
            selectedDate={selectedDate}
            events={events}
            onEventClick={handleEventClick}
            onDateChange={handleDayDateChange}
          />
        )}
      </main>

      {smartInputOpen && (
        <SmartInput
          selectedDate={selectedDate}
          editingEvent={editingEvent}
          onSave={handleSaveInput}
          onClose={() => { setSmartInputOpen(false); setEditingEvent(null); }}
        />
      )}

      {detailEvent && (
        <EventDetail
          event={detailEvent}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
          onClose={() => setDetailEvent(null)}
        />
      )}
    </div>
  );
}
